import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { useEffect } from 'react';
import { useMemoOne } from 'use-memo-one';
import { useQuery } from '@apollo/react-hooks';
import { isGQLGatewayError, isGQLUnderlyingServiceError } from '../graphql/types';
import { useMetricsContext } from '../../context';
import { getExtensionListQuery, getExtensionListQueryOld } from './lib/queries';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { OPERATIONAL_EVENT_TYPE } from '@atlaskit/analytics-gas-types';
import { FORGE_UI_ANALYTICS_CHANNEL } from '../../analytics';
export var createExtensionListQueryOptions = function createExtensionListQueryOptions(client, contextIds, queryOptions, expandAppOwner, filter, extensionType, dataClassificationTagIds, locale) {
  var variables = filter ? {
    contextIds: contextIds,
    locale: locale,
    filter: filter
  } : {
    contextIds: contextIds,
    locale: locale,
    type: extensionType
  };
  var query = filter ? getExtensionListQuery(expandAppOwner, dataClassificationTagIds) : getExtensionListQueryOld(expandAppOwner, undefined, dataClassificationTagIds);
  return _objectSpread(_objectSpread({
    variables: variables
  }, queryOptions), {}, {
    query: query,
    client: client,
    /**
     * We don't want to set 'ignore' because that discards errors and we need to inspect
     * them for SLI reporting. We also need 'all' so we don't discard data on errors:http://go/j/AUX-317
     * https://www.apollographql.com/docs/react/data/error-handling/
     */
    errorPolicy: 'all'
  });
};
export var getExtensionsFromGQLResult = function getExtensionsFromGQLResult(_ref) {
  var loading = _ref.loading,
    data = _ref.data;
  if (!loading && data && data.extensionContexts && data.extensionContexts[0]) {
    if ('extensions' in data.extensionContexts[0]) {
      var _data$extensionContex;
      return (_data$extensionContex = data.extensionContexts[0].extensions) !== null && _data$extensionContex !== void 0 ? _data$extensionContex : null;
    } else if ('extensionsByType' in data.extensionContexts[0]) {
      var _data$extensionContex2;
      return (_data$extensionContex2 = data.extensionContexts[0].extensionsByType) !== null && _data$extensionContex2 !== void 0 ? _data$extensionContex2 : null;
    }
  }
  return null;
};
export var useExtensionList = function useExtensionList(options) {
  var contextIds = options.contextIds,
    extensionType = options.type,
    filter = options.filter,
    client = options.client,
    queryOptions = options.queryOptions,
    expandAppOwner = options.expandAppOwner,
    dataClassificationTagIds = options.dataClassificationTagIds,
    locale = options.locale;
  var contextIdsString = contextIds.sort().join('');
  var startTimeMs = useMemoOne(function () {
    return performance.now();
  }, [contextIdsString, extensionType, client]);
  var requestOptions = createExtensionListQueryOptions(client, contextIds, queryOptions, expandAppOwner, filter, extensionType, dataClassificationTagIds, locale);
  var query = filter ? getExtensionListQuery(expandAppOwner) : getExtensionListQueryOld(expandAppOwner);
  var _useQuery = useQuery(requestOptions.query || query, requestOptions),
    error = _useQuery.error,
    loading = _useQuery.loading,
    data = _useQuery.data;
  var extensions = getExtensionsFromGQLResult({
    loading: loading,
    data: data
  });
  var _useMetricsContext = useMetricsContext(),
    page = _useMetricsContext.page;
  var _useAnalyticsEvents = useAnalyticsEvents(),
    createAnalyticsEvent = _useAnalyticsEvents.createAnalyticsEvent;
  useEffect(function () {
    if (extensions) {
      createAnalyticsEvent({
        eventType: OPERATIONAL_EVENT_TYPE,
        data: {
          action: 'succeeded',
          actionSubject: 'forge.ui.webClient',
          source: page,
          tags: ['forge'],
          attributes: {
            target: 'useExtensionList'
          }
        }
      }).fire(FORGE_UI_ANALYTICS_CHANNEL);
    }
  }, [extensions, startTimeMs, page, createAnalyticsEvent]);
  useEffect(function () {
    if (
    // if hydration of appOwners (non-critical data) fails but we still have the extensions,
    // we shouldn't count this as a bad event
    !extensions && error && error.graphQLErrors.length > 0) {
      var err = error.graphQLErrors.find(function (err) {
        return isGQLUnderlyingServiceError(err) || isGQLGatewayError(err);
      });
      createAnalyticsEvent({
        eventType: OPERATIONAL_EVENT_TYPE,
        data: {
          action: 'failed',
          actionSubject: 'forge.ui.webClient',
          source: page,
          tags: ['forge'],
          attributes: {
            target: 'useExtensionList',
            errorName: err ? isGQLUnderlyingServiceError(err) ? 'componentApi' : 'componentGraphQL' : 'uncaught'
          }
        }
      }).fire(FORGE_UI_ANALYTICS_CHANNEL);
    }
  }, [extensions, error, page, createAnalyticsEvent]);
  return {
    error: error,
    extensions: extensions || [],
    loading: loading
  };
};